/**
 * Where a newly added node lands on the workflow builder canvas.
 *
 * The builder's auto-layout (`computeLayout` in ../components/WorkflowTemplates)
 * layers nodes by longest path from a root, so a node with no edges yet is a
 * root and would be drawn on row 0 — level with the graph's entry points rather
 * than under the node whose `+` port was clicked. That is exactly the bug the
 * "Add node" port menu had: the new node appeared beside its intended parent,
 * and only dropped a row once an edge was drawn to it.
 *
 * The fix is to place the node through the layout's `hints.row`, a per-node row
 * floor the layout honours before laying anything out. This module computes the
 * amended hints; it is pure so the placement rules can be tested directly.
 */

/** Mirrors the layout's synthesizer uid; it is layered separately from agents. */
const SYNTH_UID = "__synth__";

/**
 * Amended `{ row, slot }` hints that put `newUid` on the level the author
 * pointed at, in the anchor's column.
 *
 * `dir` is the port that was clicked:
 *   - `"left"` / `"right"` → the anchor's own level, in the slot beside it. The
 *     row still has to be pinned: an unwired node is a layout root, so without a
 *     pin it lands on row 0 — above its anchor — no matter how the node list is
 *     ordered.
 *   - `"bottom"` / `"top"` → the adjacent level. **Join that level if it already
 *     exists**, and only create a new one when it does not. Rows are compacted
 *     to be consecutive, so a row exists exactly when some node already sits on
 *     it — which makes "does the level below exist?" a lookup, not a guess.
 *
 * Always minting a fresh level was the wrong default: adding a node under an
 * agent that already had children pushed it onto a level of its own between two
 * populated ones, stretching the graph vertically for no reason when it belonged
 * alongside its siblings.
 *
 * Returns `hints` unchanged when there is nothing to pin (no hints yet, no
 * intent, a sideways intent, or an anchor the hints don't know about).
 */
export function placeNodeHints(hints, intent, newUid, descendants = null) {
  if (!hints || !intent || !newUid) return hints;

  const anchorRow = hints.row?.[intent.anchor];
  if (anchorRow === undefined) return hints;

  const sideways = intent.dir === "left" || intent.dir === "right";

  /*
   * A sideways node needs its row pinned just as much as a vertical one. It has
   * no edges yet, so the layering treats it as a root and puts it on row 0 —
   * the level *above* its anchor — however the node list is ordered. Splicing it
   * next to the anchor only sets its left-to-right slot within whatever row it
   * lands on; it cannot choose the row.
   */
  if (sideways) {
    const slotOf = { ...hints.slot };
    const anchorSlot = slotOf[intent.anchor];
    if (anchorSlot !== undefined) {
      // sit immediately beside the anchor; the layout re-indexes the row after
      slotOf[newUid] = anchorSlot + (intent.dir === "left" ? -0.5 : 0.5);
    }
    return {
      ...hints,
      row: { ...hints.row, [newUid]: anchorRow },
      slot: slotOf,
      /*
       * Record the anchor here too. `anchorCol` is what tells the layout this
       * node was dropped from a port rather than being a graph entry point;
       * without it a sideways node has no incoming edge, counts as a root, and
       * gets a START arrow drawn to it from the top of the canvas. The vertical
       * branch below has always recorded it — this one did not, so only
       * left/right adds sprouted that stray edge.
       */
      anchorCol: { ...hints.anchorCol, [newUid]: intent.anchor },
    };
  }

  const row = { ...hints.row };
  const target = intent.dir === "bottom" ? anchorRow + 1 : anchorRow - 1;
  // The synthesizer is re-pinned to the last agent row + 1 on every layout, so
  // it never really holds a level open — an agent placed on its row just pushes
  // it down. Ignore it here so the decision matches what the layout will do.
  const levelExists = Object.entries(hints.row)
    .some(([u, r]) => r === target && u !== SYNTH_UID);

  if (levelExists) {
    // The level is already there — join it rather than wedging a new one in.
    row[newUid] = target;
  } else if (intent.dir === "bottom") {
    // Below the deepest row: appending one is all that is needed.
    row[newUid] = target;
  } else {
    /*
     * Above the topmost row, so there is no level to take — one has to be made.
     *
     * Only the anchor moves down, not the whole graph. Shifting every node meant
     * that adding above one of three siblings dropped all three a level, so two
     * nodes the author never touched lost their place at the top (and with it
     * their START arrows). The anchor's own descendants follow it, because a
     * node must still sit below its parents; anything on another branch is left
     * exactly where it was.
     *
     * The anchor then shares a row with its former siblings' children rather
     * than with the siblings themselves, which is what "the new node takes the
     * anchor's place at the top" actually means.
     */
    const moving = new Set([intent.anchor]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const [child, parentList] of Object.entries(descendants || {})) {
        if (moving.has(child)) continue;
        if (parentList.some((p) => moving.has(p))) { moving.add(child); grew = true; }
      }
    }
    moving.forEach((u) => { if (row[u] !== undefined) row[u] += 1; });
    row[newUid] = anchorRow;
  }

  // Share the anchor's column so the node lands directly under (or over) it
  // instead of at the end of its row. `slot` orders the row; `anchorCol` is what
  // actually pins the x, because rows are centred independently and a slot index
  // alone would let a short row drift out from under its anchor.
  const slot = { ...hints.slot };
  if (slot[intent.anchor] !== undefined) slot[newUid] = slot[intent.anchor];

  const anchorCol = { ...hints.anchorCol };
  anchorCol[newUid] = intent.anchor;

  // spread the incoming hints rather than rebuilding them: they also carry the
  // remembered columns and held rows, and dropping those re-flows the graph on
  // the very edit that was supposed to leave it alone
  return { ...hints, row, slot, anchorCol };
}

export default placeNodeHints;
